import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import { Flex } from '../Flex';

/**
 * Props for the `ListWithControls` component.
 *
 * @template T - The type of the items in the list.
 *
 * @property {T[]} items - The array of items to be displayed in the list.
 * @property {(index: number, item: T, isSelected?: boolean) => React.ReactNode} renderItem - A function to render each item in the list.
 * @property {() => void} onLoadMore - Callback function triggered when more items need to be loaded.
 * @property {string} height - The height of the list container.
 * @property {string} width - The width of the list container.
 * @property {React.ReactNode} [controls] - Optional controls to display alongside the list.
 * @property {React.ReactNode} [footerContent] - Optional content to display in the footer of the list.
 * @property {number} [loadThreshold] - Optional threshold (in pixels) to trigger the `onLoadMore` callback when scrolling.
 * @property {boolean} [isLoading] - Optional flag indicating whether the list is currently loading more items.
 * @property {string} [className] - Optional CSS class name for the list container.
 * @property {React.CSSProperties} [styles] - Optional inline styles for the list container.
 * @property {(item: T, index: number) => void} [onItemFocus] - Optional callback when an item is foucsed via keyboard.
 */
export interface ListWithControlsProps<T> {
  items: T[];
  renderItem: (index: number, item: T, isSelected?: boolean) => React.ReactNode;
  onLoadMore: () => void;
  height: string;
  width: string;
  controls?: React.ReactNode;
  footerContent?: React.ReactNode;
  loadThreshold?: number;
  isLoading?: boolean;
  className?: string;
  styles?: React.CSSProperties;
  onItemFocus?: (item: T, index: number) => void;
}

/**
 * ListWithControls is a component that renders a list of items with controls above it.
 * It uses the Virtuoso library for virtualization, which allows for efficient rendering of large lists.
 * The component takes in a list of items, a render function for each item, and a function to load more items when the user scrolls to the bottom of the list.
 * It also accepts props for height, width, and loading state.
 *
 * @param {ListWithControlsProps<T>} props - The props for the ListWithControls component.
 * @returns {JSX.Element} - The rendered ListWithControls component.
 */
function ListWithControls<T>({
  items,
  renderItem,
  onLoadMore,
  height,
  width,
  controls,
  footerContent,
  loadThreshold = 100,
  isLoading = false,
  className = '',
  styles = {},
  onItemFocus,
}: ListWithControlsProps<T>) {
  const ref = useRef<VirtuosoHandle>(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
  const listRef = useRef<HTMLElement | null>(null);

  // Focus handler to select first item when list receives focus
  const handleFocus = useCallback(() => {
    if (focusedItemIndex === -1 && items.length > 0) {
      setFocusedItemIndex(0);
    }
  }, [focusedItemIndex, items.length]);

  const handleOutOfFocus = useCallback(() => {
    setFocusedItemIndex(-1);
  }, []);

  // Reset selection when items change significantly
  useEffect(() => {
    if (focusedItemIndex >= items.length) {
      setFocusedItemIndex(items.length > 0 ? 0 : -1);
    }
  }, [items.length, focusedItemIndex]);

  const keyDownCallback = useCallback(
    (e: KeyboardEvent) => {
      let nextIndex = null;

      if (e.code === 'ArrowUp') {
        nextIndex = Math.max(0, focusedItemIndex - 1);
      } else if (e.code === 'ArrowDown') {
        nextIndex = Math.min(items.length - 1, focusedItemIndex + 1);
      } else if (
        (e.code === 'Enter' || e.code === 'Space') &&
        focusedItemIndex >= 0
      ) {
        // Trigger item selection when Enter or Space is pressed
        if (onItemFocus && items[focusedItemIndex]) {
          onItemFocus(items[focusedItemIndex], focusedItemIndex);
          e.preventDefault();
        }
        return;
      }

      if (nextIndex !== null && ref.current) {
        ref.current.scrollIntoView({
          index: nextIndex,
          behavior: 'auto',
          done: () => {
            setFocusedItemIndex(nextIndex);
          },
        });
        e.preventDefault();
      }
    },
    [focusedItemIndex, items, onItemFocus]
  );
  const scrollerRef = useCallback(
    (element: HTMLElement | null | Window) => {
      if (element instanceof HTMLElement) {
        element.addEventListener('keydown', keyDownCallback);
        element.addEventListener('focus', handleFocus);
        element.setAttribute('tabIndex', '0');
        listRef.current = element;
      } else if (listRef.current) {
        listRef.current.removeEventListener('keydown', keyDownCallback);
        listRef.current.removeEventListener('focus', handleFocus);
      }
    },
    [keyDownCallback, handleFocus]
  );
  return (
    <Flex
      col
      className={`listControlsContainer ${className ?? ''}`}
      style={{ height: height, width: width, ...styles }} // Set a height/width or else Virtuoso will not render on the page.
      dataTestId="list-with-controls"
    >
      {/* Render optional controls above the list */}
      {controls && <Flex className="listControls">{controls}</Flex>}
      <Flex
        className="listItems"
        style={{ height: '100%', width: '100%', overflow: 'auto' }}
      >
        <Virtuoso
          data={items}
          endReached={!isLoading ? onLoadMore : undefined}
          atBottomThreshold={loadThreshold}
          itemContent={(index, item) =>
            renderItem(index, item, index === focusedItemIndex)
          }
          components={{
            Footer: () => (isLoading ? <>{footerContent}</> : null), // Render footer content if loading
          }}
          style={{ height: '100%', width: '100%', overflow: 'auto' }} // Ensure Virtuoso takes up full container space
          initialTopMostItemIndex={0}
          ref={ref}
          scrollerRef={scrollerRef}
          onBlur={handleOutOfFocus}
        />
      </Flex>
    </Flex>
  );
}

export default ListWithControls;
